/**
 * The complete permission vocabulary of the LMS.
 *
 * This file is the single source of truth. It is seeded into the `permissions`
 * table, rendered as the Admin permission-matrix UI, and enforced by
 * `requirePermission()` on the API. Adding a capability means adding a key here
 * first — nothing checks a permission string that does not exist in this list.
 *
 * SCOPE PERMISSIONS (the `scope.*` module) are special: they decide how much
 * data a role can see, not which buttons it has. Access control resolves them
 * in priority order — organization beats department beats self — which is what
 * lets an Admin build a custom role with any reach without touching code.
 */
export const PERMISSION_MODULES = [
    'scope',
    'user',
    'role',
    'department',
    'designation',
    'category',
    /* Library content as one thing — the blanket over courses, assessments and
       questions alike, which belongs to none of them individually. */
    'content',
    'course',
    'resource',
    'learning',
    'assessment',
    'question',
    'challenge',
    'evaluation',
    'rebuttal',
    'certificate',
    'report',
    'announcement',
    'calendar',
    'audit',
    'settings',
];
export const PERMISSIONS = [
    // ── Scope ────────────────────────────────────────────────────────────────
    // Exactly one of these effectively applies; the highest held one wins.
    { key: 'scope.organization', module: 'scope', description: 'See and act on data across the entire organization' },
    { key: 'scope.department', module: 'scope', description: 'See and act on data only within managed departments' },
    { key: 'scope.self', module: 'scope', description: 'See and act on own data only' },
    // ── Users ────────────────────────────────────────────────────────────────
    { key: 'user.view', module: 'user', description: 'View users' },
    { key: 'user.create', module: 'user', description: 'Create users' },
    { key: 'user.edit', module: 'user', description: 'Edit user details' },
    { key: 'user.disable', module: 'user', description: 'Enable or disable user accounts' },
    { key: 'user.delete', module: 'user', description: 'Delete users' },
    { key: 'user.import', module: 'user', description: 'Bulk import users from Excel' },
    { key: 'user.assign_role', module: 'user', description: 'Assign roles to users' },
    { key: 'user.assign_department', module: 'user', description: 'Assign departments a user belongs to or manages' },
    { key: 'user.reset_password', module: 'user', description: "Reset another user's password" },
    // ── Roles & permissions ──────────────────────────────────────────────────
    { key: 'role.view', module: 'role', description: 'View roles' },
    { key: 'role.create', module: 'role', description: 'Create custom roles' },
    { key: 'role.edit', module: 'role', description: 'Edit roles' },
    { key: 'role.delete', module: 'role', description: 'Delete custom roles' },
    { key: 'role.manage_permissions', module: 'role', description: 'Enable or disable permissions on a role' },
    // ── Org structure ────────────────────────────────────────────────────────
    { key: 'department.view', module: 'department', description: 'View departments' },
    { key: 'department.create', module: 'department', description: 'Create departments' },
    { key: 'department.edit', module: 'department', description: 'Edit departments' },
    { key: 'department.delete', module: 'department', description: 'Delete departments' },
    { key: 'designation.view', module: 'designation', description: 'View designations' },
    { key: 'designation.create', module: 'designation', description: 'Create designations' },
    { key: 'designation.edit', module: 'designation', description: 'Edit designations' },
    { key: 'designation.delete', module: 'designation', description: 'Delete designations' },
    { key: 'category.view', module: 'category', description: 'View course categories' },
    { key: 'category.manage', module: 'category', description: 'Create, edit and delete course categories' },
    // ── Courses ──────────────────────────────────────────────────────────────
    /**
     * `course.delete` was deliberately absent — the original decision was that
     * courses are only ever archived. Reversed on request (2026-09-05): admins
     * asked for full control over the things they create.
     *
     * It is a SOFT delete and archive still exists, because they are not the
     * same act. Archiving withdraws a course everybody keeps: it stays in
     * reports, completions still count, certificates still name it. Deleting
     * removes it from the product — a mistake, a duplicate, a draft nobody
     * should see. Both are recoverable in the database; neither destroys a
     * certificate that was already issued.
     */
    { key: 'course.view', module: 'course', description: 'View all courses in the admin catalogue' },
    { key: 'course.create', module: 'course', description: 'Create courses' },
    { key: 'course.edit', module: 'course', description: 'Edit courses' },
    { key: 'course.publish', module: 'course', description: 'Publish and unpublish courses' },
    { key: 'course.archive', module: 'course', description: 'Withdraw a course from everyone assigned it' },
    { key: 'course.delete', module: 'course', description: 'Delete courses' },
    { key: 'course.assign', module: 'course', description: 'Assign courses to users' },
    { key: 'course.bulk_assign', module: 'course', description: 'Assign courses in bulk from Excel' },
    { key: 'course.complete', module: 'course', description: 'Sign off completion of manually-completed courses' },
    { key: 'resource.view', module: 'resource', description: 'View course resources in the builder' },
    { key: 'resource.upload', module: 'resource', description: 'Upload files and add links to courses' },
    { key: 'resource.edit', module: 'resource', description: 'Edit and reorganise folders and resources' },
    { key: 'resource.delete', module: 'resource', description: 'Delete folders and resources' },
    // ── Own learning (every role that learns needs these) ────────────────────
    { key: 'learning.view_own', module: 'learning', description: 'Access own assigned courses and progress' },
    { key: 'learning.browse_catalogue', module: 'learning', description: 'Browse the optional course catalogue' },
    { key: 'learning.self_enroll', module: 'learning', description: 'Self-enroll in catalogue courses' },
    { key: 'learning.wishlist', module: 'learning', description: 'Add courses to wishlist' },
    { key: 'learning.rate', module: 'learning', description: 'Rate courses' },
    // ── Assessments ──────────────────────────────────────────────────────────
    { key: 'assessment.view', module: 'assessment', description: 'View assessments' },
    { key: 'assessment.create', module: 'assessment', description: 'Create assessments' },
    { key: 'assessment.edit', module: 'assessment', description: 'Edit assessments' },
    { key: 'assessment.publish', module: 'assessment', description: 'Publish and unpublish assessments' },
    { key: 'assessment.delete', module: 'assessment', description: 'Delete assessments' },
    { key: 'assessment.assign', module: 'assessment', description: 'Assign assessments to users' },
    { key: 'assessment.bulk_assign', module: 'assessment', description: 'Assign assessments in bulk from Excel' },
    { key: 'assessment.take', module: 'assessment', description: 'Take assigned assessments' },
    { key: 'assessment.analytics', module: 'assessment', description: 'View assessment analytics' },
    /**
     * `question.edit` and `question.delete` are deliberately absent.
     *
     * A question is immutable once created, except for its answer key — that is
     * what keeps the denormalised `Assessment.totalMarks` honest and what stops
     * a question's weighting moving under the assessments already using it.
     * Questions are attached and detached, never edited or deleted, so both
     * toggles offered authority over actions that do not exist.
     */
    { key: 'question.view', module: 'question', description: 'View the question bank' },
    { key: 'question.create', module: 'question', description: 'Create questions' },
    { key: 'question.import', module: 'question', description: 'Bulk import questions from Excel' },
    { key: 'question.edit_answer_key', module: 'question', description: 'Change a correct answer, triggering re-evaluation of affected attempts' },
    /**
     * ── Today's Learning Spark ───────────────────────────────────────────────
     *
     * Its own pair rather than folding into `question.*`. These are not exam
     * questions — they never contribute to a mark — and somebody trusted to
     * write a daily prompt is not necessarily somebody trusted with the graded
     * question bank. Keeping them separate lets a custom role do one without
     * the other.
     */
    { key: 'challenge.view', module: 'challenge', description: "View Today's Learning Spark questions and results" },
    { key: 'challenge.manage', module: 'challenge', description: "Write and schedule Today's Learning Spark questions" },
    // ── Evaluation ───────────────────────────────────────────────────────────
    { key: 'evaluation.view_queue', module: 'evaluation', description: 'View the pending manual-evaluation queue' },
    { key: 'evaluation.evaluate', module: 'evaluation', description: 'Grade descriptive answers and submit results' },
    /**
     * Its own key, deliberately NOT folded into `evaluation.evaluate`.
     *
     * Marking and destroying are not the same authority. A Manager marks their
     * own team's papers, and this discards a candidate's submitted work — and
     * the screenshots behind it — before anybody has looked at it. Granting that
     * with the same tick as "can grade" would hand it to every marker in the
     * organisation.
     *
     * Admin-only in the presets, and it is the only permission in this module
     * that is.
     */
    { key: 'evaluation.delete_attempt', module: 'evaluation', description: 'Permanently delete a submitted attempt and the files attached to it' },
    /**
     * ── Rebuttals ──────────────────────────────────────────────────────────
     * A candidate's objection to a question. Its own pair rather than folding
     * into `evaluation.*`: answering "is this question fair" is a judgement
     * about the PAPER, and the person who wrote the paper is the one to make
     * it — which is not necessarily the person marking the answers.
     */
    { key: 'rebuttal.view', module: 'rebuttal', description: 'View objections candidates raised against questions' },
    { key: 'rebuttal.respond', module: 'rebuttal', description: 'Answer a question objection and close it' },
    // ── Certificates ─────────────────────────────────────────────────────────
    { key: 'certificate.view', module: 'certificate', description: 'View issued certificates' },
    { key: 'certificate.issue', module: 'certificate', description: 'Manually issue certificates' },
    { key: 'certificate.bulk_issue', module: 'certificate', description: 'Issue certificates in bulk from Excel' },
    { key: 'certificate.revoke', module: 'certificate', description: 'Revoke issued certificates' },
    // ── Reporting ────────────────────────────────────────────────────────────
    { key: 'report.view', module: 'report', description: 'View reports and analytics' },
    { key: 'report.export', module: 'report', description: 'Export reports to Excel, CSV or PDF' },
    // ── Communication ────────────────────────────────────────────────────────
    { key: 'announcement.view', module: 'announcement', description: 'View announcements' },
    { key: 'announcement.create', module: 'announcement', description: 'Create announcements' },
    { key: 'announcement.edit', module: 'announcement', description: 'Edit announcements' },
    { key: 'announcement.delete', module: 'announcement', description: 'Delete announcements' },
    { key: 'calendar.view', module: 'calendar', description: 'View the calendar' },
    { key: 'calendar.create', module: 'calendar', description: 'Create calendar events' },
    { key: 'calendar.edit', module: 'calendar', description: 'Edit calendar events' },
    { key: 'calendar.delete', module: 'calendar', description: 'Delete calendar events' },
    // ── Operations ───────────────────────────────────────────────────────────
    { key: 'audit.view', module: 'audit', description: 'View activity and audit logs' },
    { key: 'settings.view', module: 'settings', description: 'View system settings' },
    { key: 'settings.edit', module: 'settings', description: 'Change system settings' },
    /**
     * Ticking "this course awards a certificate".
     *
     * Split out from `certificate.issue`, which it used to share. The two are
     * different authorities and the difference is the whole of the Trainer role:
     *
     *   issue / bulk_issue   hand an award to a named person who may not have
     *                        earned it. A deliberate act, one at a time or four
     *                        hundred from a sheet.
     *
     *   award_via_course     decide that FINISHING THE WORK earns one. The
     *                        course's own completion rule then does the issuing,
     *                        and nobody receives anything they did not complete.
     *
     * Conflating them meant a Trainer could build the course and not switch its
     * certificate on — or could only do so by being handed the power to mint
     * certificates for anyone, which is the thing being withheld.
     */
    {
        key: 'certificate.award_via_course',
        module: 'certificate',
        description: 'Set a course to award a certificate on completion',
    },
    /**
     * The blanket over other people's courses, assessments and questions.
     *
     * Organisation scope used to imply this, which was true while the Admin was
     * the only organisation-wide role. A Trainer is org-wide too and must be
     * able to see and assign the Admin's mandatory training without being able
     * to change a word of it — so the blanket became a permission of its own
     * rather than a side effect of seeing everybody.
     */
    {
        key: 'content.manage_all',
        module: 'content',
        description: "Edit and delete courses and assessments created by others",
    },
];
export const PERMISSION_KEYS = PERMISSIONS.map((p) => p.key);
export const PERMISSION_SET = new Set(PERMISSION_KEYS);
export function isPermissionKey(value) {
    return PERMISSION_SET.has(value);
}
// ═══════════════════════════════════════════════════════════════════════════
// Scope ranking
// ═══════════════════════════════════════════════════════════════════════════
/**
 * Scope permissions are ordered, not independent.
 *
 * The escalation guard normally refuses to grant any permission the actor does
 * not personally hold. That exact-match rule is wrong for scope: a department
 * manager holds `scope.department` but not `scope.self`, and must still be
 * able to create ordinary Employees. What actually matters is that nobody
 * grants a scope WIDER than their own.
 */
export const SCOPE_RANK = {
    'scope.self': 1,
    'scope.department': 2,
    'scope.organization': 3,
};
export function isScopePermission(key) {
    return key in SCOPE_RANK;
}
/** The widest scope a set of permissions confers. 0 means none. */
export function maxScopeRank(permissions) {
    let rank = 0;
    for (const key of permissions) {
        const value = SCOPE_RANK[key];
        if (value !== undefined && value > rank)
            rank = value;
    }
    return rank;
}
// ═══════════════════════════════════════════════════════════════════════════
// System role presets
// ═══════════════════════════════════════════════════════════════════════════
export const SYSTEM_ROLES = {
    ADMIN: 'admin',
    MANAGER: 'manager',
    TRAINER: 'trainer',
    EMPLOYEE: 'employee',
};
/** Everything an ordinary learner needs. Manager and Admin both build on it. */
const EMPLOYEE_PERMISSIONS = [
    'scope.self',
    'learning.view_own',
    'learning.browse_catalogue',
    'learning.self_enroll',
    'learning.wishlist',
    'learning.rate',
    'assessment.take',
    'announcement.view',
    'calendar.view',
];
/**
 * A Manager is an Employee plus department governance. Note what is absent:
 * settings, `category.manage`, `question.edit_answer_key`, and — by request —
 * certificates.
 *
 * **Certificates are an Admin act, on every path that reaches one.** A
 * certificate is the compliance record this whole system exists to produce,
 * and issuing one asserts that training happened without the system having
 * watched it happen. `certificate.view` and `certificate.issue` are both off,
 * which takes the Certificates screen off a Manager's menu — but the permission
 * list alone was not enough, because a certificate can be reached three ways
 * and only one of them is the obvious button:
 *
 *   1. Issuing one by hand         — `certificate.issue`, off.
 *   2. Setting a course to award   — refused by `assertMayAwardCertificates`
 *      them (`certificateEnabled`)   in courses.routes.ts. The bigger of the
 *                                     two: one tick certifies everybody who
 *                                     finishes, now and forever.
 *   3. Signing off a MANUAL course — refused by `completeCourseManually`
 *      that awards one                unless the actor can issue.
 *
 * A Manager may still ASSIGN a certificate-awarding course an Admin built, and
 * their team will earn certificates by completing it. That is the Admin's
 * course behaving as the Admin configured it, which is the point of assigning
 * training — not the Manager exercising discretion over who gets certified.
 *
 * **Managers author their own content, and only their own.** They hold the
 * full build set below — create, edit, publish, archive, delete, resources,
 * questions. What keeps that safe is NOT the permission list, because courses
 * and assessments are not department-scoped: a Manager with `course.view`
 * already sees every course in the organisation, so `course.edit` alone would
 * reach the Admin's mandatory compliance training.
 *
 * The second gate is authorship — see `lib/authorship.ts` in the API. Every
 * mutating course/assessment/question route also asserts the caller created
 * the thing, unless their scope is ORGANIZATION. A Manager therefore builds
 * freely and cannot touch what somebody else built.
 *
 * Deliberately still absent, because neither scopes down to a department:
 *   `category.manage`         — one shared org-wide list; renaming a category
 *                               changes it for all 30 departments.
 *   `question.edit_answer_key` — re-scores every existing attempt org-wide,
 *                               including other departments'.
 */
const MANAGER_PERMISSIONS = [
    ...EMPLOYEE_PERMISSIONS.filter((p) => p !== 'scope.self'),
    'scope.department',
    'user.view',
    'department.view',
    'designation.view',
    'category.view',
    'course.view',
    'course.create',
    'course.edit',
    'course.publish',
    'course.archive',
    'course.delete',
    'course.assign',
    'course.bulk_assign',
    /**
     * A course with no content in it is not a course. These four come with
     * `course.create` or the build set is unusable — and each is gated on the
     * authorship of the OWNING course, so they reach only a Manager's own.
     */
    'resource.view',
    'resource.upload',
    'resource.edit',
    'resource.delete',
    /**
     * Kept, but no longer a way to reach a certificate.
     *
     * A MANUAL course is completed by a person and nothing else, so withholding
     * this would leave every MANUAL course a Manager assigns waiting on an
     * administrator to sign off, one learner at a time.
     *
     * The authority is split by CONSEQUENCE rather than by course: an ordinary
     * MANUAL course, a Manager signs off freely; one that awards a certificate
     * needs `certificate.issue` as well, and `completeCourseManually` refuses
     * without it. Signing somebody off on a certificate-awarding course is
     * issuing them a certificate, whatever the button says.
     */
    'course.complete',
    'assessment.view',
    'assessment.create',
    'assessment.edit',
    'assessment.publish',
    'assessment.delete',
    'assessment.assign',
    'assessment.bulk_assign',
    'assessment.analytics',
    /**
     * An assessment with no questions in it is not an assessment. `question.view`
     * opens the bank so a Manager can reuse what exists; `question.create` is
     * how they write their own. Detaching a question from an assessment is
     * `assessment.edit` and so follows the assessment's authorship; editing a
     * question follows the question's own.
     */
    'question.view',
    'question.create',
    /**
     * The same authority as `question.create`, through a spreadsheet.
     *
     * A Manager writing a thirty-question paper by hand in the builder, one
     * question at a time, is doing the job the bulk importer exists for — and
     * the importer grants nothing they did not already have: every imported
     * question is stamped with their own `createdById`, and the spec refuses
     * outright if they lack `question.create`.
     *
     * It needed one fix first. Importing INTO an assessment is driven by an
     * `assessmentId` in the bulk job's context, which is arbitrary JSON from the
     * request body — and that path never ran `requireAuthor`, because the bulk
     * engine is not the assessment routes. Harmless while only Admins imported;
     * an escalation the moment a Manager could. `question-import.spec.ts` now
     * calls `assertAuthored` before accepting the file.
     */
    'question.import',
    /**
     * Marking, on their own papers only.
     *
     * These used to be absent, with `evaluation.evaluate` described as an
     * Admin-toggleable extra. It is a Manager's job now — but the permission
     * alone would have let them mark their team's sitting of the ADMIN's
     * compliance exam, which is somebody else's standard to apply.
     *
     * The evaluation queue therefore composes two restrictions at once:
     * `scopedAttemptWhere` for whose attempt it is, and
     * `authoredAssessmentAttemptWhere` for whose paper it is. A Manager sees and
     * marks only their own team, on only their own assessments. See
     * `evaluation.service.ts`.
     */
    'evaluation.view_queue',
    'evaluation.evaluate',
    /**
     * Objections on their own papers, for the same reason as marking: the
     * person who wrote the question is the one who can say whether it was fair.
     * Scoped by authorship in the service, exactly as the evaluation queue is.
     */
    'rebuttal.view',
    'rebuttal.respond',
    'report.view',
    'report.export',
    'calendar.create',
    'calendar.edit',
    'calendar.delete',
];
/**
 * Admin holds every permission, including every future one added to this file
 * and all three scope levels.
 *
 * Holding several scope permissions is harmless — `resolveScope` takes the
 * highest — and holding *all* of them is necessary: the escalation guard
 * refuses to grant a role containing any permission the actor lacks, so an
 * Admin missing `scope.self` could not create an Employee.
 */
/**
 * ═══════════════════════════════════════════════════════════════════════════
 * TRAINER — builds for the whole organisation, administers none of it
 *
 * Organisation-wide like an Admin: every department, every designation, every
 * employee. Unlike a Manager, a Trainer is not tied to departments they
 * govern — they write training for the company.
 *
 * Defined by SUBTRACTION from the full set, deliberately. An allow-list would
 * silently withhold every permission added after today, and the next feature
 * would appear for Admins and be invisible to Trainers with nobody noticing.
 * Naming what a Trainer must NOT have means a new permission is theirs by
 * default and taking it away is a decision somebody writes down.
 *
 * ── What is withheld, and why ─────────────────────────────────────────────
 *
 *   content.manage_all   The one that makes this role work. A Trainer SEES
 *                        the Admin's courses and assessments — they have to,
 *                        to assign them — and cannot change a word of them.
 *                        Their own, they own entirely.
 *
 *   user.* (beyond view) People administration is not training. `user.view`
 *                        stays, because assigning a course to somebody means
 *                        finding them first; creating, editing, disabling,
 *                        deleting, importing and resetting passwords do not.
 *
 *   role.*               Including `role.view`: a role screen that lists the
 *                        permission matrix and cannot change it is an
 *                        invitation to ask why.
 *
 *   certificate.*        ALL of it, by request — view, issue, bulk issue,
 *                        revoke, and setting a course to award one.
 *
 *                        `certificate.award_via_course` was briefly granted,
 *                        on the reading that a Trainer should be able to build
 *                        a course that awards. That was withdrawn: certificates
 *                        are not a Trainer's to decide at all. The permission
 *                        stays in the catalogue — it is a real distinction and
 *                        an Admin holds it — it is simply not theirs.
 *
 *                        Their OWN certificates, earned as a learner, are
 *                        unaffected: `/my-certificates` needs no permission.
 *
 *   settings.edit        One shared runtime configuration for all 30
 *                        departments. Viewing it is fine.
 *
 *   department/designation create/edit/delete
 *                        The org chart is somebody else's to shape. A Trainer
 *                        reads it — every picker in the product needs that.
 * ═══════════════════════════════════════════════════════════════════════════
 */
const TRAINER_WITHHELD = [
    'content.manage_all',
    'user.create',
    'user.edit',
    'user.disable',
    'user.delete',
    'user.import',
    'user.assign_role',
    'user.assign_department',
    'user.reset_password',
    'role.view',
    'role.create',
    'role.edit',
    'role.delete',
    'role.manage_permissions',
    'certificate.view',
    'certificate.issue',
    'certificate.bulk_issue',
    'certificate.revoke',
    'certificate.award_via_course',
    /**
     * Destroying a candidate's submitted work, and the screenshots with it.
     *
     * A Trainer marks and writes papers; discarding somebody's sitting before
     * anybody has read it is an administrator's call. Withheld here rather than
     * simply omitted, because `TRAINER_PERMISSIONS` is everything MINUS this
     * list — a new key is granted to a Trainer by default, so anything they
     * should not hold has to be named.
     */
    'evaluation.delete_attempt',
    'settings.edit',
    'department.create',
    'department.edit',
    'department.delete',
    'designation.create',
    'designation.edit',
    'designation.delete',
    /* One scope, or `resolveScope` would read the first it finds and the role
       would depend on the order of a list. */
    'scope.department',
    'scope.self',
];
const TRAINER_PERMISSIONS = PERMISSION_KEYS.filter((key) => !TRAINER_WITHHELD.includes(key));
const ADMIN_PERMISSIONS = [...PERMISSION_KEYS];
export const SYSTEM_ROLE_PRESETS = {
    [SYSTEM_ROLES.ADMIN]: {
        name: 'Admin',
        description: 'Full organization-wide access to every module and setting.',
        permissions: ADMIN_PERMISSIONS,
    },
    [SYSTEM_ROLES.MANAGER]: {
        name: 'Manager',
        description: 'Full employee learning experience plus management of assigned departments only.',
        permissions: MANAGER_PERMISSIONS,
    },
    [SYSTEM_ROLES.TRAINER]: {
        name: 'Trainer',
        description: 'Builds courses and assessments for the whole organisation. Sees everything, '
            + 'changes only what they created, and administers neither people nor roles.',
        permissions: TRAINER_PERMISSIONS,
    },
    [SYSTEM_ROLES.EMPLOYEE]: {
        name: 'Employee',
        description: 'Access to own learning, catalogue, assessments and certificates.',
        permissions: EMPLOYEE_PERMISSIONS,
    },
};
//# sourceMappingURL=permissions.js.map